import Avatar from 'core-system/Avatar/Avatar'
import PrimaryButton from 'core-system/Button/PrimaryButton'
import SecondaryButton from 'core-system/Button/SecondaryButton'
import Checkbox from 'core-system/Checkbox'
import Dropdown from 'core-system/Dropdown'
import FlexContainer from 'core-system/FlexContainer'
import Modal, { ModalBody, ModalFooter, ModalHeader } from 'core-system/Modal'
import Text from 'core-system/Text'
import palette from 'core-system/Themes/palette'
import pxToRem from 'core-system/utils/pxToRem'
import React, { useEffect, useMemo, useState } from 'react'
import {
  EmployeeTransitPass,
  TransitAgency,
} from 'redux/transitPass/transitPassTypes'
import styled from 'styled-components'
import {
  getFirstAvailableAgencyId,
  getOpenTransitAgencyIds,
} from './transitCardUtils'

const Subtitle = styled(Text)`
  margin-top: -0.5rem;
  margin-bottom: 1.25rem;
`

const CommuterCard = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.875rem 1rem;
  border: 1px solid ${palette.grey.grey300};
  border-radius: 0.75rem;
  margin-bottom: 1.25rem;
`

const AgencyField = styled.div`
  margin-bottom: 1.25rem;
`

const AmountRow = styled(FlexContainer)`
  align-items: center;
  gap: 0.75rem;
  margin-top: 0.75rem;
  margin-bottom: 0.75rem;
`

const AmountInput = styled.input`
  ${(props) => props.theme.typography.action1};
  width: 5.5rem;
  height: 2.5rem;
  padding: 0.5rem 0.75rem;
  border: 1px solid ${palette.grey.grey300};
  border-radius: 0.5rem;
  outline: none;

  &:focus {
    border-color: ${palette.text.secondary};
  }
`

const FooterActions = styled.div`
  display: flex;
  justify-content: flex-end;
  width: 100%;
  gap: 0.75rem;
`

interface RequestTransitCardModalProps {
  open: boolean
  commuterName: string
  commuterEmail?: string
  worksiteName?: string
  agencies?: TransitAgency[]
  transitPasses?: EmployeeTransitPass[]
  isSubmitting?: boolean
  onClose: () => void
  onRequest: (payload: {
    transitAgencyId: string
    addFunds: boolean
    amount: number | null
  }) => void
}

const RequestTransitCardModal = React.memo(
  (props: RequestTransitCardModalProps) => {
    const {
      open,
      commuterName,
      commuterEmail,
      worksiteName,
      agencies = [],
      transitPasses = [],
      isSubmitting = false,
      onClose,
      onRequest,
    } = props

    const [selectedAgencyId, setSelectedAgencyId] = useState('')
    const [addFunds, setAddFunds] = useState(true)
    const [amount, setAmount] = useState(50)

    const blockedAgencyIds = useMemo(
      () => getOpenTransitAgencyIds(transitPasses),
      [transitPasses]
    )

    useEffect(() => {
      if (!open) {
        return
      }
      setSelectedAgencyId(getFirstAvailableAgencyId(agencies, transitPasses))
      setAddFunds(true)
      setAmount(50)
    }, [open]) // eslint-disable-line react-hooks/exhaustive-deps

    const agencyItems = agencies.map((agency) => {
      const isBlocked = blockedAgencyIds.has(agency.id)
      return {
        id: agency.id,
        text: isBlocked ? `${agency.name} (Requested)` : agency.name,
        disabled: isBlocked,
      }
    })

    const commuterDetails = [commuterEmail, worksiteName]
      .filter(Boolean)
      .join(' · ')

    const selectedAgencyBlocked = Boolean(
      selectedAgencyId && blockedAgencyIds.has(selectedAgencyId)
    )
    const isRequestDisabled =
      isSubmitting ||
      !selectedAgencyId ||
      selectedAgencyBlocked ||
      (addFunds && (!amount || amount <= 0))

    return (
      <Modal open={open} onClose={onClose} width={pxToRem(480)}>
        <ModalHeader title='Request Transit Card' onClose={onClose} />
        <ModalBody>
          <Subtitle variant='body1' textColor={palette.text.secondary}>
            We&apos;ll find an available card and assign it to this commuter.
          </Subtitle>
          <CommuterCard>
            <Avatar name={commuterName} size='medium' />
            <FlexContainer flexDirection='column'>
              <Text variant='action2'>{commuterName}</Text>
              {commuterDetails ? (
                <Text variant='body2' textColor={palette.text.secondary}>
                  {commuterDetails}
                </Text>
              ) : null}
            </FlexContainer>
          </CommuterCard>
          <AgencyField>
            <Dropdown
              label='Transit agency'
              placeholder='Select an agency...'
              active={selectedAgencyId}
              items={agencyItems}
              itemCallback={(item) => {
                if (item.disabled) {
                  return
                }
                setSelectedAgencyId(item.id)
              }}
              width='100%'
            />
          </AgencyField>
          <FlexContainer
            onClick={() => setAddFunds((prev) => !prev)}
            style={{ cursor: 'pointer' }}
          >
            <Checkbox checked={addFunds} label='Add funds to card' />
          </FlexContainer>
          {addFunds ? (
            <AmountRow>
              <Text variant='action3'>Amount $</Text>
              <AmountInput
                type='number'
                min={1}
                value={amount}
                onChange={(event) => setAmount(Number(event.target.value))}
                aria-label='Amount'
              />
            </AmountRow>
          ) : null}
          <Text variant='body2' textColor={palette.text.placeholder}>
            Funds are added after the card is assigned. Admin, commuter, and
            support are emailed on completion.
          </Text>
        </ModalBody>
        <ModalFooter>
          <FooterActions>
            <SecondaryButton onClick={onClose} disabled={isSubmitting}>
              Cancel
            </SecondaryButton>
            <PrimaryButton
              onClick={() =>
                onRequest({
                  transitAgencyId: selectedAgencyId,
                  addFunds,
                  amount: addFunds ? amount : null,
                })
              }
              disabled={isRequestDisabled}
              isLoading={isSubmitting}
            >
              Request Card
            </PrimaryButton>
          </FooterActions>
        </ModalFooter>
      </Modal>
    )
  }
)

if (process.env.NODE_ENV !== 'production') {
  RequestTransitCardModal.displayName = 'RequestTransitCardModal'
}

export default RequestTransitCardModal
